import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = '/api/invoices';

export const fetchInvoices = createAsyncThunk('invoices/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.get(API_URL, { params });
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const createInvoice = createAsyncThunk('invoices/create', async (data, { rejectWithValue }) => {
  try {
    const response = await axios.post(API_URL, data);
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

const invoiceSlice = createSlice({
  name: 'invoices',
  initialState: {
    items: [],
    // ZATCA compliance state keyed by invoice id, sent alongside `data`.
    // Separate from the invoice rows because zatca_submissions is polymorphic
    // and has no association to Invoice — it cannot be nested by the query.
    zatca: {},
    // Is this tenant live on ZATCA Phase 2? Drives whether the compliance
    // column belongs on screen at all — a Phase-1 shop owes no filing, so a
    // "Not submitted" badge on every row reads as a failure it does not have.
    //
    // null, not false, until the first response: false would hide the column
    // for a genuine Phase-2 tenant during the first paint, which is the
    // direction that loses a real compliance signal.
    zatcaPhase2: null,
    loading: false,
    error: null,
    // Which fetch the list is currently waiting on. The list is searched from
    // the screen now, so two fetches are routinely in flight — and without
    // this, a slower broad-term response landing after the narrow one it
    // preceded overwrites the rows the user is actually looking at. The
    // symptom is a search that shows nothing, which is indistinguishable from
    // the client-side-filtering bug this replaced.
    latestRequestId: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchInvoices.pending, (state, action) => {
      state.latestRequestId = action.meta.requestId;
    });
    builder.addCase(fetchInvoices.fulfilled, (state, action) => {
      if (state.latestRequestId && action.meta.requestId !== state.latestRequestId) return;
      state.items = action.payload.data;
      // Default to {} — an older backend omits the key, and leaving the previous
      // page's map in place would label the new rows with the old page's
      // compliance states.
      state.zatca = action.payload.zatca || {};
      // An older backend omits the key entirely. Treat that as "unknown", which
      // the screen resolves toward SHOWING the column — the pre-existing
      // behaviour, and the safe direction for a compliance signal.
      state.zatcaPhase2 =
        action.payload.zatca_phase2 === undefined ? null : Boolean(action.payload.zatca_phase2);
    });
  },
});

export default invoiceSlice.reducer;
